/**
 * Google Tag Manager dataLayer events for paid-traffic measurement.
 *
 * The GTM container (loaded from index.html only when VITE_GTM_ID is set)
 * listens for these events and fires the GA4 and Google Ads tags, subject to
 * the visitor's consent (Google Consent Mode v2 — see lib/consent.ts). Without
 * GTM the pushes land in a plain array and do nothing.
 *
 * Privacy: no email, phone or name ever goes into a GA4 event. The only PII is
 * `enhanced_conversion_data` on `generate_lead`, which only the Google Ads tag
 * reads (Google hashes it before sending), and it is cleared from the
 * dataLayer model straight after by `lead_pii_clear`.
 *
 * Umami (cookieless, our own analytics) keeps its own `lead_submitted` event
 * via lib/analytics.ts; these helpers do not replace it.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type CtaLocation = "header" | "hero" | "hero_card" | "sticky" | "footer";

function push(event: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

/** Indian numbers to E.164 (+91…), as Google's enhanced conversions expect. */
export function toE164(phone: string | undefined): string | undefined {
  if (!phone) return undefined;
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return undefined;
  if (trimmed.startsWith("+")) return `+${digits}`;
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `+91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return `+${digits}`;
}

/** First interaction with a form (once per form per page view). */
export function trackFormStart(formId: string, leadService?: string): void {
  push({ event: "form_start", form_id: formId, lead_service: leadService });
}

export interface LeadConversion {
  formId: string;
  leadId: string;
  email: string;
  phone?: string;
  leadService?: string;
  leadTimeline?: string;
  pageVariant?: string;
}

/**
 * The conversion. Call ONLY after the API stored the lead and returned its id
 * — never on validation errors, server errors, or a submission the server
 * silently dropped as spam (no id) — so Google Ads counts real leads only. The
 * lead id doubles as the transaction id, which de-duplicates repeat fires.
 */
export function trackGenerateLead(c: LeadConversion): void {
  push({
    event: "generate_lead",
    form_id: c.formId,
    lead_id: c.leadId,
    lead_service: c.leadService,
    lead_timeline: c.leadTimeline,
    page_variant: c.pageVariant,
    enhanced_conversion_data: {
      email: c.email.trim().toLowerCase(),
      phone_number: toE164(c.phone),
    },
  });
  // Remove the PII from GTM's data model so no later tag can read it.
  push({ event: "lead_pii_clear", enhanced_conversion_data: undefined });
}

export function trackFormError(formId: string, errorType: "server" | "network" | "config"): void {
  push({ event: "form_error", form_id: formId, error_type: errorType });
}

export function trackCtaClick(location: CtaLocation): void {
  push({ event: "cta_click", cta_location: location });
}

export function trackEmailClick(): void {
  push({ event: "email_click" });
}

export function trackFaqOpen(question: string): void {
  push({ event: "faq_open", faq_question: question });
}
